import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { filter, take } from 'rxjs';
import { CustomObjectModels } from '@models/custom-object.model';
import { NgIf, NgFor } from '@angular/common';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { TranslatePipe } from '@ngx-translate/core';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { AuthSrcDirective } from '../../directives/auth-src.directive';
import { ThumbnailsService } from '../../../development/thumbnails.service';
import { StorageBucket } from '@consts/storage-url.util';

interface StatePreviewItem {
  url: string;
  label: string;
  hasError: boolean;
}

@Component({
  selector: 'ui-custom-object-state-card',
  templateUrl: './ui-custom-object-state-card.component.html',
  styleUrls: ['./ui-custom-object-state-card.component.scss'],
  standalone: true,
  imports: [NgIf, NgFor, MatMenuTrigger, MatMenu, MatMenuItem, TranslatePipe, AuthSrcDirective],
})
export class UiCustomObjectStateCardComponent implements OnChanges {
  @Output() editName = new EventEmitter<void>();
  @Output() retrain = new EventEmitter<void>();
  @Output() remove = new EventEmitter<void>();

  @Input() customObject: CustomObjectModels.CustomObject | CustomObjectModels.StateCustomObject;

  /**
   * Whether the card offers its edit/remove menu. Defaults to true so the
   * Database → Custom objects page keeps the actions it has always had; the
   * read-only Cmd+K panel passes false.
   */
  @Input() showActions: boolean = true;

  public imageLoadError = false;
  
  // Cached state preview items to avoid recreating on every change detection
  private _statePreviewItems: StatePreviewItem[] = [];
  private _statePreviewCount = 0;
  private orgId: string = '';

  constructor(private store$: Store, private thumbnailsService: ThumbnailsService) {
    this.store$.pipe(select(OrganizationSelectors.selectOrgId), filter(orgId => !!orgId), take(1)).subscribe(orgId => {
      this.orgId = orgId ?? '';
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customObject']) {
      this.imageLoadError = false;
      this.updateStatePreviewItems();
    }
  }

  public onImageError(): void {
    this.imageLoadError = true;
  }

  public onStateImageError(index: number): void {
    if (this._statePreviewItems[index]) {
      this._statePreviewItems[index].hasError = true;
    }
  }

  public getImagePath(): string {
    if (this.isStateCustomObject(this.customObject)) {
      const bestImage = this.customObject.perStateBestImages?.[0];
      if (!bestImage) {
        return '';
      }
      const isSnapshot = bestImage.thumType === CustomObjectModels.ML.ThumbnailType.SNAPSHOT;
      const trainingBase = this.thumbnailsService.getTrainingThumbnailsBaseUrl(bestImage.edgeId, isSnapshot);
      const subPath = isSnapshot ? 'snapshot' : 'training';
      return `${trainingBase}/${subPath}/${bestImage.edgeId}/${bestImage.cameraId}/${bestImage.thumbnail}`;
    }

    if (this.customObject?.bestImage) {
      const base = this.thumbnailsService.getBucketBaseUrl(StorageBucket.CustomObjects);
      return `${base}/crop/${this.customObject.bestImage}`;
    }
    return '';
  }

  public getDisplayTags(): string[] {
    if (!this.customObject || this.isStateCustomObject(this.customObject)) return [
      'CUSTOM_OBJECTS.TAGS.STATE_ALERTS',
      'CUSTOM_OBJECTS.TAGS.SEARCH'
    ];
    
    // Determine tags based on object type
    const tags: string[] = [];
    
    // For PERSON, VEHICLE, PET - show "Appearance alert" and "Search"
    if (
      this.customObject.type === CustomObjectModels.ObjectType.PERSON ||
      this.customObject.type === CustomObjectModels.ObjectType.VEHICLE ||
      this.customObject.type === CustomObjectModels.ObjectType.PET
    ) {
      tags.push('CUSTOM_OBJECTS.TAGS.ALL_OBJECTS_ALERTS');
      tags.push('CUSTOM_OBJECTS.TAGS.SEARCH');
    }
    
    return tags;
  }

  public onEditName(): void {
    this.editName.emit();
  }

  public onRetrain(): void {
    this.retrain.emit();
  }

  public onRemove(): void {
    this.remove.emit();
  }

  public getDisplayName(): string {
    if (!this.customObject) {
      return '';
    }

    if (this.isStateCustomObject(this.customObject)) {
      const name = this.customObject.name?.trim();
      const statesLabel = this.customObject.states?.join('/') ?? '';
      return name ? `${name}: ${statesLabel}` : statesLabel;
    }

    return this.customObject.name ?? '';
  }

  /**
   * Returns cached state preview items - only updated when customObject changes
   */
  public get statePreviewItems(): StatePreviewItem[] {
    return this._statePreviewItems;
  }

  /**
   * Returns cached count - only updated when customObject changes
   */
  public get statePreviewCount(): number {
    return this._statePreviewCount;
  }

  /**
   * Updates the cached state preview items when the customObject changes
   */
  private updateStatePreviewItems(): void {
    if (!this.customObject || !this.isStateCustomObject(this.customObject)) {
      this._statePreviewItems = [];
      this._statePreviewCount = 0;
      return;
    }

    const states = this.customObject.states ?? [];
    const images = this.customObject.perStateBestImages ?? [];

    const customObjBase = this.thumbnailsService.getBucketBaseUrl(StorageBucket.CustomObjects);
    this._statePreviewItems = states.slice(0, 4).map((state, index) => {
      const image = images[index];
      const url = `${customObjBase}/crop/${this.orgId}/${image?.thumbnail || ''}`;
      return {
        url,
        label: state,
        hasError: false,
      };
    });
    
    this._statePreviewCount = this._statePreviewItems.length;
  }

  public isStateCustomObject(
    customObject: CustomObjectModels.CustomObject | CustomObjectModels.StateCustomObject,
  ): customObject is CustomObjectModels.StateCustomObject {
    return (customObject as CustomObjectModels.StateCustomObject)?.states !== undefined;
  }

  public trackByIndex(index: number): number {
    return index;
  }
}
